/**
 * Реестр инструментов разметки.
 *
 * Инструмент описывает себя целиком: имя, группа, сколько точек нужно,
 * горячая клавиша. Ни панель, ни обработчик мыши, ни рисовальщик не
 * содержат перечня инструментов — они читают отсюда. Добавление нового
 * инструмента это запись в этом файле плюс ветка в рисовальщике.
 *
 * Группы повторяют раскладку, к которой человек привык в терминалах:
 * линии, каналы, Фибоначчи, прогноз позиции, фигуры, стрелки, кисти,
 * текст, иконки, диапазоны.
 */

export type DrawingKind =
  // линии
  | 'trend'
  | 'ray'
  | 'extended'
  | 'info-line'
  | 'trend-angle'
  | 'horizontal'
  | 'horizontal-ray'
  | 'vertical'
  | 'cross'
  | 'level-zone'
  // каналы
  | 'parallel-channel'
  | 'flat-channel'
  | 'disjoint-channel'
  | 'regression'
  | 'pitchfork'
  | 'schiff-pitchfork'
  // паттерны и волны
  | 'triangle-pattern'
  | 'head-shoulders'
  | 'double-pattern'
  | 'elliott-impulse'
  | 'elliott-correction'
  | 'xabcd'
  // Фибоначчи
  | 'fib-retracement'
  | 'fib-extension'
  | 'fib-fan'
  | 'fib-time'
  | 'fib-channel'
  | 'fib-arcs'
  // Ганн
  | 'gann-fan'
  | 'gann-box'
  // прогноз позиции
  | 'long-position'
  | 'short-position'
  | 'projection'
  | 'forecast-path'
  | 'dca-ladder'
  // на основе объёма
  | 'anchored-vwap'
  | 'volume-profile-range'
  | 'volume-profile-anchored'
  // оповещения на графике
  | 'alert-zone'
  | 'alert-line'
  // фигуры
  | 'rect'
  | 'circle'
  | 'ellipse'
  | 'triangle'
  | 'polyline'
  | 'path'
  // стрелки
  | 'arrow'
  | 'arrow-curve'
  | 'arrow-up'
  | 'arrow-down'
  // кисти
  | 'brush'
  | 'highlighter'
  // текст и заметки
  | 'text'
  | 'note'
  | 'price-label'
  | 'callout'
  | 'flag'
  | 'trade-plan'
  | 'icon'
  // диапазоны и измерение
  | 'price-range'
  | 'date-range'
  | 'measure';

export type ToolGroupId =
  | 'cursors'
  | 'lines'
  | 'patterns'
  | 'alerts'
  | 'fib'
  | 'forecast'
  | 'shapes'
  | 'brushes'
  | 'text'
  | 'icons'
  | 'measure';

export interface KindSpec {
  readonly kind: DrawingKind;
  readonly name: string;
  readonly group: ToolGroupId;
  /**
   * Сколько точек ставит человек.
   *
   * `free` — рисование от руки: точки набираются, пока держат кнопку.
   * `many` — щелчок за щелчком, сколько угодно точек: заканчивают
   * двойным щелчком, щелчком по последней точке или Enter.
   */
  readonly points: number | 'free' | 'many';
  /** Подсказка вида «Alt + T». */
  readonly hotkey?: string;
  /** Требует ввода текста перед началом. */
  readonly needsText?: boolean;
  /** Не сохраняется в профиль: измерение живёт до следующего клика. */
  readonly transient?: boolean;
  /**
   * Достраивает фигуру из поставленных точек.
   *
   * Нужен там, где человек задаёт меньше точек, чем есть у фигуры.
   * Прогноз позиции рисуют одним движением — от входа к стопу, — а цель
   * ставится сама и потом двигается за угол. Три клика подряд для этого
   * инструмента оказались неудобными: было непонятно, какая точка чем
   * стала.
   */
  readonly derive?: (points: readonly DerivedPoint[], kind: DrawingKind) => DerivedPoint[];
}

/** Точка в том виде, в каком её знает реестр: время и цена. */
export interface DerivedPoint {
  readonly time: number;
  readonly price: number;
}

/**
 * Отношение прибыли к риску у новой позиции.
 *
 * Единица — нейтральная отправная точка: цель ровно на том же
 * расстоянии, что и стоп. Любое другое число было бы предположением о
 * замысле сделки, а его человек выражает сам — вторым движением и
 * перетаскиванием уровней.
 */
export const DEFAULT_POSITION_RATIO = 1;

/**
 * Достройка прогноза позиции: стоп и цель по направлению сделки.
 *
 * Направление задаёт выбранный инструмент, а не сторона протяжки. У
 * длинной позиции стоп всегда ниже входа, а цель выше; у короткой
 * наоборот. От мыши берётся только расстояние, по модулю.
 *
 * Выправляются обе цены, а не только недостающая: пока правилась одна
 * лишь предлагаемая, в заготовке оставалась сырая точка мыши, и на
 * последнем клике длинная позиция переворачивалась в короткую.
 */
function derivePosition(points: readonly DerivedPoint[], kind: DrawingKind): DerivedPoint[] {
  const entry = points[0];
  const stopInput = points[1];
  if (!entry || !stopInput) return [...points];

  const long = kind !== 'short-position';
  const side = (distance: number): number =>
    long ? entry.price + distance : entry.price - distance;

  const risk = Math.abs(entry.price - stopInput.price);
  const stop = { time: stopInput.time, price: side(-risk) };

  const targetInput = points[2];
  const reward = targetInput
    ? Math.abs(entry.price - targetInput.price)
    : risk * DEFAULT_POSITION_RATIO;

  return [
    entry,
    stop,
    { time: targetInput?.time ?? stopInput.time, price: side(reward) },
  ];
}

export const KINDS: readonly KindSpec[] = [
  // --- линии
  { kind: 'trend', name: 'Линия тренда', group: 'lines', points: 2, hotkey: 'Alt + T' },
  { kind: 'ray', name: 'Луч', group: 'lines', points: 2 },
  { kind: 'extended', name: 'Удлинённая линия', group: 'lines', points: 2 },
  { kind: 'info-line', name: 'Линия данных', group: 'lines', points: 2 },
  { kind: 'trend-angle', name: 'Угол тренда', group: 'lines', points: 2 },
  {
    kind: 'horizontal',
    name: 'Горизонтальная линия',
    group: 'lines',
    points: 1,
    hotkey: 'Alt + H',
  },
  {
    kind: 'horizontal-ray',
    name: 'Горизонтальный луч',
    group: 'lines',
    points: 1,
    hotkey: 'Alt + J',
  },
  { kind: 'vertical', name: 'Вертикальная линия', group: 'lines', points: 1, hotkey: 'Alt + V' },
  { kind: 'cross', name: 'Перекрещенные линии', group: 'lines', points: 1, hotkey: 'Alt + C' },
  // Уровень не тонкой линией, а полосой: поддержка — это зона цен.
  { kind: 'level-zone', name: 'Уровень-зона', group: 'lines', points: 2 },

  // --- каналы
  { kind: 'parallel-channel', name: 'Параллельный канал', group: 'lines', points: 3 },
  { kind: 'flat-channel', name: 'Плоский верх/низ', group: 'lines', points: 3 },
  { kind: 'disjoint-channel', name: 'Расходящийся канал', group: 'lines', points: 3 },
  { kind: 'regression', name: 'Регрессионный тренд', group: 'lines', points: 2 },
  { kind: 'pitchfork', name: 'Вилы Эндрюса', group: 'lines', points: 3 },
  { kind: 'schiff-pitchfork', name: 'Вилы Шиффа', group: 'lines', points: 3 },

  // --- паттерны и волны
  { kind: 'triangle-pattern', name: 'Треугольник', group: 'patterns', points: 4 },
  { kind: 'head-shoulders', name: 'Голова и плечи', group: 'patterns', points: 7 },
  { kind: 'double-pattern', name: 'Двойная вершина / дно', group: 'patterns', points: 5 },
  { kind: 'elliott-impulse', name: 'Импульс Эллиотта 1–5', group: 'patterns', points: 6 },
  { kind: 'elliott-correction', name: 'Коррекция Эллиотта A–B–C', group: 'patterns', points: 4 },
  { kind: 'xabcd', name: 'Гармонический XABCD', group: 'patterns', points: 5 },

  // --- Фибоначчи
  {
    kind: 'fib-retracement',
    name: 'Коррекция Фибоначчи',
    group: 'fib',
    points: 2,
    hotkey: 'Alt + F',
  },
  { kind: 'fib-extension', name: 'Расширение Фибоначчи по тренду', group: 'fib', points: 3 },
  { kind: 'fib-fan', name: 'Веерные линии Фибоначчи', group: 'fib', points: 2 },
  { kind: 'fib-time', name: 'Периоды времени Фибоначчи', group: 'fib', points: 2 },
  { kind: 'fib-channel', name: 'Канал Фибоначчи', group: 'fib', points: 3 },
  { kind: 'fib-arcs', name: 'Дуги Фибоначчи', group: 'fib', points: 2 },
  { kind: 'gann-fan', name: 'Веер Ганна', group: 'fib', points: 2 },
  { kind: 'gann-box', name: 'Сетка Ганна', group: 'fib', points: 2 },

  // --- прогноз
  /*
   * Три цены: вход, стоп, цель. Первым движением задают вход и стоп —
   * бокс появляется сразу целиком. Отпустил — цель идёт за мышью, и
   * отношение риска к прибыли меняется на глазах; клик закрепляет её.
   *
   * Одним движением три цены не задать: отношение требует третьей, и
   * пока цель вычислялась из стопа, оно было вечной двойкой.
   */
  {
    kind: 'long-position',
    name: 'Длинная позиция',
    group: 'forecast',
    points: 3,
    derive: derivePosition,
  },
  {
    kind: 'short-position',
    name: 'Короткая позиция',
    group: 'forecast',
    points: 3,
    derive: derivePosition,
  },

  { kind: 'projection', name: 'Проекция прошлого движения', group: 'forecast', points: 3 },
  { kind: 'forecast-path', name: 'Путь прогноза', group: 'forecast', points: 2 },
  { kind: 'dca-ladder', name: 'Вход лесенкой', group: 'forecast', points: 3 },

  /*
   * На основе объёма — считаются по свечам графика (volume-tools.ts).
   * Привязка — одна точка: от её свечи до последней. Диапазон — две точки
   * по времени; цена точек в счёте не участвует.
   */
  { kind: 'anchored-vwap', name: 'VWAP с привязкой', group: 'forecast', points: 1 },
  { kind: 'volume-profile-range', name: 'Профиль объёма в диапазоне', group: 'forecast', points: 2 },
  { kind: 'volume-profile-anchored', name: 'Профиль объёма с привязкой', group: 'forecast', points: 1 },

  // --- оповещения на графике: рисунок, который сторожит сервер
  { kind: 'alert-zone', name: 'Моя зона — оповещение', group: 'alerts', points: 2 },
  { kind: 'alert-line', name: 'Линия-оповещение', group: 'alerts', points: 2 },

  // --- фигуры
  { kind: 'rect', name: 'Прямоугольник', group: 'brushes', points: 2, hotkey: 'Alt + Shift + R' },
  { kind: 'circle', name: 'Окружность', group: 'brushes', points: 2 },
  { kind: 'ellipse', name: 'Эллипс', group: 'brushes', points: 2 },
  { kind: 'triangle', name: 'Треугольник', group: 'brushes', points: 3 },
  // Ломаная и траектория — щелчок за щелчком прямыми отрезками. От руки
  // они рисовались как кисть, и «ломаная» выходила кривой.
  { kind: 'polyline', name: 'Ломаная линия', group: 'brushes', points: 'many' },
  { kind: 'path', name: 'Траектория', group: 'brushes', points: 'many' },

  // --- стрелки
  { kind: 'arrow', name: 'Стрелка', group: 'brushes', points: 2 },
  // Третья опора выгибает стрелку дугой: движение редко идёт по прямой.
  { kind: 'arrow-curve', name: 'Изогнутая стрелка', group: 'brushes', points: 3 },
  // Две точки: первая — остриё у бара, вторая задаёт длину. Стрелка
  // фиксированного размера не годится — на разных масштабах она то
  // теряется, то закрывает свечу.
  { kind: 'arrow-up', name: 'Стрелка вверх', group: 'brushes', points: 2 },
  { kind: 'arrow-down', name: 'Стрелка вниз', group: 'brushes', points: 2 },

  // --- кисти
  { kind: 'brush', name: 'Кисть', group: 'brushes', points: 'free' },
  { kind: 'highlighter', name: 'Маркер', group: 'brushes', points: 'free' },

  // --- текст и заметки
  { kind: 'text', name: 'Текст', group: 'text', points: 1, needsText: true },
  { kind: 'note', name: 'Заметка', group: 'text', points: 1, needsText: true },
  { kind: 'price-label', name: 'Метка цены', group: 'text', points: 1 },
  { kind: 'callout', name: 'Сноска', group: 'text', points: 2, needsText: true },
  { kind: 'flag', name: 'Флаг', group: 'text', points: 1 },
  // Текст плана вписывается в окне настроек, которое открывается сразу:
  // четыре строки в системное окно браузера не уместить.
  { kind: 'trade-plan', name: 'План сделки', group: 'text', points: 1 },
  { kind: 'icon', name: 'Стикер', group: 'icons', points: 1 },

  // --- диапазоны и измерение
  { kind: 'price-range', name: 'Диапазон цены', group: 'brushes', points: 2 },
  { kind: 'date-range', name: 'Диапазон времени', group: 'brushes', points: 2 },
  // Линейка стоит отдельной кнопкой, поэтому её семейство ни на одной
  // кнопке панели не отображается.
  { kind: 'measure', name: 'Линейка', group: 'measure', points: 2, transient: true },
];

const BY_KIND = new Map<string, KindSpec>(KINDS.map((spec) => [spec.kind, spec]));

export function findKind(kind: string): KindSpec | undefined {
  return BY_KIND.get(kind);
}

/** Сколько точек нужно, чтобы фигура состоялась. */
export function pointsNeeded(kind: DrawingKind): number {
  const spec = BY_KIND.get(kind);
  if (!spec) return 2;
  // Рисование от руки заканчивается отпусканием кнопки, а не счётом
  // точек: минимум — две, иначе получилась бы точка.
  return spec.points === 'free' || spec.points === 'many' ? 2 : spec.points;
}

/** Щелчок за щелчком, сколько угодно точек — до двойного щелчка или Enter. */
export function isMultiClick(kind: DrawingKind): boolean {
  return BY_KIND.get(kind)?.points === 'many';
}

export function isFreehand(kind: DrawingKind): boolean {
  return BY_KIND.get(kind)?.points === 'free';
}

export interface ToolGroup {
  readonly id: ToolGroupId;
  readonly name: string;
  /** Подписи разделов внутри выпадающего меню. */
  readonly sections: ReadonlyArray<{ label: string; kinds: readonly DrawingKind[] }>;
}

/** Раскладка боковой панели: одна кнопка — одна группа. */
export const TOOL_GROUPS: readonly ToolGroup[] = [
  {
    id: 'lines',
    name: 'Линии',
    sections: [
      {
        label: 'Линии',
        kinds: [
          'trend',
          'ray',
          'extended',
          'info-line',
          'trend-angle',
          'horizontal',
          'horizontal-ray',
          'vertical',
          'cross',
          'level-zone',
        ],
      },
      /*
       * Каналы и вилы — в «Линиях»: это те же линии, только парами, и
       * искать их отдельной кнопкой было лишним шагом.
       */
      {
        label: 'Каналы',
        kinds: ['parallel-channel', 'flat-channel', 'disjoint-channel', 'regression'],
      },
      { label: 'Вилы', kinds: ['pitchfork', 'schiff-pitchfork'] },
    ],
  },
  {
    id: 'alerts',
    name: 'Оповещения на графике',
    sections: [{ label: 'Сервер разбудит, когда цена дойдёт', kinds: ['alert-zone', 'alert-line'] }],
  },
  {
    id: 'patterns',
    name: 'Паттерны и волны',
    sections: [
      { label: 'Фигуры графика', kinds: ['triangle-pattern', 'head-shoulders', 'double-pattern'] },
      { label: 'Волны', kinds: ['elliott-impulse', 'elliott-correction'] },
      { label: 'Гармоники', kinds: ['xabcd'] },
    ],
  },
  {
    id: 'fib',
    name: 'Фибоначчи и Ганн',
    sections: [
      {
        label: 'Фибоначчи',
        kinds: ['fib-retracement', 'fib-extension', 'fib-fan', 'fib-time', 'fib-channel', 'fib-arcs'],
      },
      { label: 'Ганн', kinds: ['gann-fan', 'gann-box'] },
    ],
  },
  {
    id: 'forecast',
    name: 'Прогноз',
    sections: [
      { label: 'Позиции', kinds: ['long-position', 'short-position', 'dca-ladder'] },
      { label: 'Прогноз', kinds: ['projection', 'forecast-path'] },
      { label: 'На основе объёма', kinds: ['anchored-vwap', 'volume-profile-range', 'volume-profile-anchored'] },
    ],
  },
  {
    id: 'brushes',
    name: 'Фигуры и кисти',
    sections: [
      { label: 'Кисти', kinds: ['brush', 'highlighter'] },
      // Траектория — стрелка по ломаной: её ищут среди стрелок.
      { label: 'Стрелки', kinds: ['arrow', 'arrow-curve', 'path', 'arrow-up', 'arrow-down'] },
      { label: 'Фигуры', kinds: ['rect', 'circle', 'ellipse', 'triangle', 'polyline'] },
      { label: 'Диапазоны', kinds: ['price-range', 'date-range'] },
    ],
  },
  {
    id: 'text',
    name: 'Текст и заметки',
    sections: [
      { label: 'Текст и заметки', kinds: ['text', 'note', 'price-label', 'callout', 'flag', 'trade-plan'] },
    ],
  },
  {
    id: 'icons',
    name: 'Стикеры',
    sections: [{ label: 'Стикеры', kinds: ['icon'] }],
  },
];

/**
 * Есть ли у инструмента отдельные настройки.
 *
 * Признак живёт в реестре, а не в панели: иначе шестерёнка и окно
 * настроек расходятся — ровно это и случилось, когда прогноз позиции
 * получил настройки, а кнопку к ним нет.
 */
export function hasSettings(kind: DrawingKind): boolean {
  return SETTINGS_KINDS.has(kind);
}

const SETTINGS_KINDS = new Set<DrawingKind>([
  'arrow-curve',
  'text',
  'note',
  'callout',
  // У флага теперь надпись на полотнище — её и настраивают.
  'flag',
  'rect',
  // У регрессии — цвета половин канала и его ширина.
  'regression',
  // Линии, каналы и фигуры с подписью — у них вкладка «Текст».
  'trend',
  'ray',
  'extended',
  'info-line',
  'trend-angle',
  'horizontal',
  'horizontal-ray',
  'vertical',
  'parallel-channel',
  'flat-channel',
  'disjoint-channel',
  'circle',
  'ellipse',
  'path',
  'polyline',
  'trade-plan',
  'arrow',
  'arrow-up',
  'arrow-down',
  'fib-retracement',
  'fib-extension',
  'fib-fan',
  'fib-time',
  'long-position',
  'short-position',
  'anchored-vwap',
  'volume-profile-range',
  'volume-profile-anchored',
]);

/**
 * Линейка стоит на панели отдельной кнопкой.
 *
 * Ей пользуются чаще всего остального: «сколько прошло и за сколько
 * времени» спрашивают постоянно. Прятать её в список — значит два
 * движения вместо одного на самом частом действии.
 */
export const STANDALONE_KIND: DrawingKind = 'measure';

/**
 * Влияет ли толщина линии на вид фигуры.
 *
 * У подписи, иконки и флага линий нет вовсе. У стрелок толщина влияет:
 * от неё зависит ширина древка и головы.
 */
export function usesLineWidth(kind: DrawingKind): boolean {
  return !NO_WIDTH.has(kind);
}

/**
 * Влияет ли стиль линии — сплошная, пунктир, точки.
 *
 * Закрашенные фигуры штриховать нечем: у стрелки и флага нет контура,
 * который можно сделать пунктирным. Измерительные инструменты рисуют
 * свою штриховку по смыслу, и чужая настройка её только сломала бы.
 * Показывать выбор там, где он ничего не меняет, — обманывать.
 */
export function usesLineStyle(kind: DrawingKind): boolean {
  return !NO_WIDTH.has(kind) && !NO_STYLE.has(kind);
}

// У стикера толщина — это размер.
// У стикера размер — рамкой на графике, а не толщиной линии.
// У профилей объёма линий нет: столбцы и уровни — их вид в настройках.
const NO_WIDTH = new Set<DrawingKind>(['text', 'flag', 'note', 'price-label', 'trade-plan', 'icon', 'volume-profile-range', 'volume-profile-anchored']);

const NO_STYLE = new Set<DrawingKind>([
  'icon',
  'arrow',
  'arrow-curve',
  'arrow-up',
  'arrow-down',
  'price-range',
  'date-range',
  'measure',
  // Линия VWAP всегда сплошная: пунктиром у неё рисуются полосы.
  'anchored-vwap',
]);
